export interface LineSeries {
  data: number[];
  color: string;
  label?: string;
}

export interface LineChartProps {
  series: LineSeries[];
  labels: string[];
  width?: number;
  height?: number;
}

export function LineChart({ series, labels, width = 540, height = 200 }: LineChartProps) {
  if (!series.length || !labels.length) return null;
  const allVals = series.flatMap((s) => s.data);
  const max = Math.max(...allVals) * 1.15;
  const min = 0;
  const padL = 32;
  const padB = 24;
  const padT = 10;
  const innerW = width - padL - 12;
  const innerH = height - padT - padB;
  const stepX = innerW / (labels.length - 1);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(max * t));

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      {ticks.map((t, i) => {
        const y = padT + innerH - (t / max) * innerH;
        return (
          <g key={i}>
            <line
              x1={padL}
              x2={width - 12}
              y1={y}
              y2={y}
              stroke="var(--dg-border)"
              strokeWidth="1"
              strokeDasharray={i === 0 ? '0' : '2 3'}
            />
            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="var(--dg-text-3)">
              {t}
            </text>
          </g>
        );
      })}
      {labels.map((l, i) => (
        <text
          key={i}
          x={padL + i * stepX}
          y={padT + innerH + 16}
          textAnchor="middle"
          fontSize="10"
          fill="var(--dg-text-3)"
        >
          {l}
        </text>
      ))}
      {series.map((s, si) => {
        const pts = s.data.map(
          (v, i) => `${padL + i * stepX},${padT + innerH - ((v - min) / max) * innerH}`,
        );
        return (
          <g key={si}>
            <path
              d={`M ${pts.join(' L ')}`}
              fill="none"
              stroke={s.color}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {s.data.map((v, i) => (
              <circle
                key={i}
                cx={padL + i * stepX}
                cy={padT + innerH - ((v - min) / max) * innerH}
                r="2.5"
                fill={s.color}
              />
            ))}
          </g>
        );
      })}
    </svg>
  );
}
